import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import LoginButton from '../components/LoginButton';
import NavHeader from '../components/NavHeader';
import { 
  Trophy, Medal, Award, ChevronUp, ExternalLink, Linkedin, 
  Calendar, ArrowLeft, Send, Loader2, Edit3, Check, X, Upload, User, Pencil, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPER_ADMIN_EMAIL = "adam.odonnell@thefounderinitiative.com";

// NavHeader component now imported from shared components

// Achievement Badge Component
const AchievementBadge = ({ achievement }) => {
  if (!achievement) return null;
  
  const badges = {
    winner: { icon: Trophy, label: 'Winner', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
    top2: { icon: Medal, label: 'Top 2', className: 'bg-slate-400/20 text-slate-300 border-slate-400/50' },
    top3: { icon: Medal, label: 'Top 3', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' },
    top10: { icon: Award, label: 'Top 10', className: 'bg-amber-700/20 text-amber-600 border-amber-700/50' },
    top15: { icon: Award, label: 'Top 15', className: 'bg-slate-700/20 text-slate-500 border-slate-700/50' },
  };
  
  const badge = badges[achievement];
  if (!badge) return null;
  
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${badge.className}`}>
      <Icon className="w-4 h-4" />
      {badge.label}
    </span>
  );
};

// Login Modal for unauthenticated users
const LoginModal = ({ isOpen, onClose, action = "continue" }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-800 max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Sign in to {action}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-slate-400 mb-6">
          Join The Founder Initiative community to upvote, comment, and engage with startups.
        </p>
        <LoginButton className="w-full" />
        <p className="text-xs text-slate-500 mt-4 text-center">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

// Claim Profile Modal - Simple email verification (email acts as password)
const ClaimModal = ({ isOpen, onClose, company, onSuccess }) => {
  const [step, setStep] = useState('email'); // email, success
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, isAuthenticated, loginWithGoogle } = useAuth();
  
  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setStep('email');
      setEmail('');
      setError('');
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleClaimWithEmail = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setError('Please sign in with Google first');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/claim/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          company_id: company.company_id, 
          email: email.trim().toLowerCase() 
        })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to claim profile');
      
      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-800 max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {step === 'success' ? 'Profile Claimed!' : `Claim ${company?.name}`}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {step === 'email' && (
          <form onSubmit={handleClaimWithEmail}>
            <p className="text-slate-400 mb-4">
              Enter the email you used to sign up for the event on Luma to verify ownership.
            </p>
            
            {!isAuthenticated && (
              <div className="mb-4 p-3 bg-blue-900/30 border border-blue-800 rounded-lg">
                <p className="text-blue-300 text-sm mb-2">Sign in with Google first:</p>
                <LoginButton className="w-full" />
              </div>
            )}
            
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={!isAuthenticated}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 mb-4 disabled:opacity-50"
              data-testid="claim-email-input"
            />
            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <Button 
              type="submit" 
              disabled={loading || !isAuthenticated || !email.trim()} 
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="claim-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Claim Profile'}
            </Button>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Your Google account will be linked as the owner of this profile.
            </p>
          </form>
        )}
        
        {step === 'success' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-slate-300 mb-2">
              You now own this profile!
            </p>
            <p className="text-slate-500 text-sm">
              Your Google account is linked - you can edit anytime.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Profile Form - Inline editing with same visual layout
const EditProfileForm = ({ company, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: company.name || '',
    tagline: company.tagline || '',
    website: company.website || '',
    solution_statement: company.solution_statement || '',
    linkedin_url: company.linkedin_url || '',
    twitter_url: company.twitter_url || '',
    category: company.category || '',
    stage: company.stage || '',
    founding_year: company.founding_year || '',
    team_size: company.team_size || '',
    funding_stage: company.funding_stage || '',
    logo_url: company.logo_url || '',
    founder: {
      name: company.founder?.name || '',
      title: company.founder?.title || '',
      avatar_url: company.founder?.avatar_url || '',
      linkedin_url: company.founder?.linkedin_url || ''
    }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFounderAvatar, setUploadingFounderAvatar] = useState(false);
  const logoInputRef = React.useRef(null);
  const founderAvatarInputRef = React.useRef(null);
  
  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    const isLogo = type === 'logo';
    if (isLogo) setUploadingLogo(true);
    else setUploadingFounderAvatar(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type', type);
      
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      
      if (isLogo) {
        setFormData(prev => ({ ...prev, logo_url: data.url }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          founder: { ...prev.founder, avatar_url: data.url }
        }));
      }
    } catch (err) {
      setError(`Failed to upload image: ${err.message}`);
    } finally {
      if (isLogo) setUploadingLogo(false);
      else setUploadingFounderAvatar(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/startups/${company.company_id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      
      onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const companyInitial = formData.name ? formData.name[0].toUpperCase() : '?';
  const avatarGradient = 'from-slate-700 to-slate-800';
  
  // Editable input style that looks like the view but is editable
  const editableInputClass = "w-full bg-transparent border-b border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-white py-1 transition-colors";
  const editableTextareaClass = "w-full bg-slate-800/50 border border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-slate-200 p-3 rounded-lg transition-colors resize-none leading-relaxed";
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Header - Editable */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-start gap-4">
              {/* Company Logo - Editable */}
              <div className="relative group">
                <input
                  type="file"
                  ref={logoInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                />
                <div 
                  onClick={() => logoInputRef.current?.click()}
                  className={`w-20 h-20 rounded-xl bg-gradient-to-br ${avatarGradient} flex items-center justify-center flex-shrink-0 border-2 border-dashed border-slate-600 hover:border-blue-500 cursor-pointer transition-colors overflow-hidden`}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  ) : formData.logo_url ? (
                    <img src={formData.logo_url} alt={formData.name} className="w-full h-full rounded-xl object-cover" />
                  ) : (
                    <span className="text-3xl font-bold text-slate-400">{companyInitial}</span>
                  )}
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <Upload className="w-5 h-5 text-white" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Company Name"
                  className={`${editableInputClass} text-2xl font-bold mb-2`}
                  data-testid="edit-company-name"
                />
                
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="Short tagline (100 characters)"
                  maxLength={100}
                  className={`${editableInputClass} text-slate-300 mb-3`}
                  data-testid="edit-tagline"
                />
                
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-slate-500" />
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://yourwebsite.com"
                      className={`${editableInputClass} text-sm w-48`}
                      data-testid="edit-website"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-slate-500" />
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      placeholder="LinkedIn URL"
                      className={`${editableInputClass} text-sm w-40`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Description - Editable */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">About</h2>
            <textarea
              value={formData.solution_statement}
              onChange={(e) => setFormData({ ...formData, solution_statement: e.target.value })}
              placeholder="Describe your company, product, and what problem you're solving..."
              rows={5}
              className={editableTextareaClass}
              data-testid="edit-description"
            />
          </div>
        </div>
        
        {/* Sidebar - Editable */}
        <div className="space-y-6">
          {/* Save/Cancel Buttons */}
          <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-4">
            <p className="text-blue-300 text-sm mb-3">Editing mode - changes are not saved until you click Save</p>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <div className="flex gap-2">
              <Button 
                type="submit" 
                disabled={saving} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel} 
                className="border-slate-600"
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Founder - Editable */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Founder</h3>
            <div className="flex items-center gap-3">
              {/* Founder Avatar - Editable */}
              <div className="relative group">
                <input
                  type="file"
                  ref={founderAvatarInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'founder')}
                />
                <div 
                  onClick={() => founderAvatarInputRef.current?.click()}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center cursor-pointer border-2 border-dashed border-transparent hover:border-blue-500 transition-colors overflow-hidden"
                >
                  {uploadingFounderAvatar ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                  ) : formData.founder.avatar_url ? (
                    <img src={formData.founder.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-white">{formData.founder.name?.[0] || '?'}</span>
                  )}
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <Upload className="w-3 h-3 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={formData.founder.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    founder: { ...formData.founder, name: e.target.value }
                  })}
                  placeholder="Founder Name"
                  className={`${editableInputClass} font-medium text-sm`}
                  data-testid="edit-founder-name"
                />
                <input
                  type="text"
                  value={formData.founder.title}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    founder: { ...formData.founder, title: e.target.value }
                  })}
                  placeholder="Title (e.g., CEO & Co-founder)"
                  className={`${editableInputClass} text-slate-400 text-sm`}
                  data-testid="edit-founder-title"
                />
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-slate-500" />
                <input
                  type="url"
                  value={formData.founder.linkedin_url}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    founder: { ...formData.founder, linkedin_url: e.target.value }
                  })}
                  placeholder="Founder LinkedIn URL"
                  className={`${editableInputClass} text-sm flex-1`}
                />
              </div>
            </div>
          </div>
          
          {/* Company Details - Editable */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Stage</span>
                <input
                  type="text"
                  value={formData.stage}
                  onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                  placeholder="e.g., Seed"
                  className="bg-transparent border-b border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-white py-0.5 text-right w-24"
                  data-testid="edit-stage"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Team Size</span>
                <input
                  type="text"
                  value={formData.team_size}
                  onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                  placeholder="e.g., 10-50"
                  className="bg-transparent border-b border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-white py-0.5 text-right w-24"
                  data-testid="edit-team-size"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Founded</span>
                <input
                  type="text"
                  value={formData.founding_year}
                  onChange={(e) => setFormData({ ...formData, founding_year: e.target.value })}
                  placeholder="e.g., 2023"
                  className="bg-transparent border-b border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-white py-0.5 text-right w-24"
                  data-testid="edit-founding-year"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Category</span>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Devtool"
                  className="bg-transparent border-b border-dashed border-slate-600 focus:border-blue-500 focus:outline-none text-white py-0.5 text-right w-24"
                  data-testid="edit-category"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

// Main Product Page Component
const StartupProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  // Get the category from URL params for back navigation
  const fromCategory = searchParams.get('from') || '';
  
  const [startup, setStartup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginAction, setLoginAction] = useState('continue');
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Check if user is super admin
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;
  
  const [userUpvoted, setUserUpvoted] = useState(false);
  const [isUpvoting, setIsUpvoting] = useState(false);
  
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Fetch startup data
  useEffect(() => {
    const fetchStartup = async () => {
      try {
        const res = await fetch(`${API_URL}/api/startups/${slug}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('Startup not found');
          } else {
            throw new Error('Failed to load startup');
          }
          return;
        }
        const data = await res.json();
        setStartup(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStartup();
  }, [slug]);
  
  // Fetch upvote status
  useEffect(() => {
    if (startup?.company_id && isAuthenticated) {
      const checkUpvote = async () => {
        try {
          const res = await fetch(`${API_URL}/api/companies/${startup.company_id}/upvote-status`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            setUserUpvoted(data.upvoted);
          }
        } catch (err) {
          console.error('Error checking upvote status:', err);
        }
      };
      checkUpvote();
    }
  }, [startup?.company_id, isAuthenticated]);
  
  // Fetch comments
  useEffect(() => {
    if (startup?.company_id) {
      const fetchComments = async () => {
        setLoadingComments(true);
        try {
          const res = await fetch(`${API_URL}/api/companies/${startup.company_id}/comments`);
          if (res.ok) {
            const data = await res.json();
            setComments(data.comments || []);
          }
        } catch (err) {
          console.error('Error fetching comments:', err);
        } finally {
          setLoadingComments(false);
        }
      };
      fetchComments();
    }
  }, [startup?.company_id]);
  
  const handleUpvote = async () => {
    if (!isAuthenticated) {
      setLoginAction('upvote');
      setShowLoginModal(true);
      return;
    }
    
    setIsUpvoting(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${startup.company_id}/upvote`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserUpvoted(data.upvoted);
        setStartup(prev => ({
          ...prev,
          upvote_count: data.upvote_count
        }));
      }
    } catch (err) {
      console.error('Error upvoting:', err);
    } finally {
      setIsUpvoting(false);
    }
  };
  
  const handleComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setLoginAction('comment');
      setShowLoginModal(true);
      return;
    }
    
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const res = await fetch(`${API_URL}/api/companies/${startup.company_id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ body: newComment })
      });
      
      if (res.ok) {
        const comment = await res.json();
        // Add new comment at the end (comments are sorted oldest first)
        setComments(prev => [...prev, comment]);
        setNewComment('');
        setStartup(prev => ({
          ...prev,
          comment_count: (prev.comment_count || 0) + 1
        }));
      }
    } catch (err) {
      console.error('Error commenting:', err);
    } finally {
      setSubmittingComment(false);
    }
  };
  
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        setStartup(prev => ({
          ...prev,
          comment_count: Math.max(0, (prev.comment_count || 0) - 1)
        }));
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };
  
  const handleEditClick = () => {
    if (!startup.is_claimed) {
      setShowClaimModal(true);
    } else if (user && startup.owner_id === user.user_id) {
      setIsEditing(true);
    }
  };
  
  const handleProfileSave = (updatedData) => {
    setStartup(prev => ({ ...prev, ...updatedData }));
    setIsEditing(false);
  };
  
  const handleClaimSuccess = () => {
    // Refresh the page to get updated data
    window.location.reload();
  };
  
  const isOwner = user && startup?.owner_id === user.user_id;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }
  
  if (error || !startup) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Startup Not Found</h1>
          <p className="text-slate-400 mb-4">The startup you're looking for doesn't exist.</p>
          <Link to="/startups">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Startups
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  // Generate company avatar
  const companyInitial = startup.name?.[0]?.toUpperCase() || '?';
  const avatarGradient = 'from-slate-700 to-slate-800';
  
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Main Navigation Header */}
      <NavHeader />
      
      {/* Back Navigation */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Link 
            to={`/startups${fromCategory ? `?category=${encodeURIComponent(fromCategory)}` : ''}`}
            onClick={(e) => {
              e.preventDefault();
              // Try to go back if we came from startups page
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                // Fall back to direct navigation with category if available
                navigate(`/startups${fromCategory ? `?category=${encodeURIComponent(fromCategory)}` : ''}`);
              }
            }}
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-startups"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Startups
          </Link>
        </div>
      </header>
      
      <main className="max-w-5xl mx-auto px-6 py-8">
        {isEditing ? (
          <EditProfileForm 
            company={startup} 
            onSave={handleProfileSave} 
            onCancel={() => setIsEditing(false)} 
          />
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Header */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  {/* Company Logo/Avatar */}
                  <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${avatarGradient} flex items-center justify-center flex-shrink-0 border border-slate-700`}>
                    {startup.logo_url ? (
                      <img src={startup.logo_url} alt={startup.name} className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      <span className="text-3xl font-bold text-slate-400">{companyInitial}</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h1 className="text-2xl font-bold text-white">{startup.name}</h1>
                      <AchievementBadge achievement={startup.selection_status} />
                    </div>
                    
                    <p className="text-slate-300 mb-3">{startup.tagline}</p>
                    
                    <div className="flex items-center gap-3 flex-wrap">
                      {startup.website && (
                        <a href={startup.website} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Visit Website
                          </Button>
                        </a>
                      )}
                      {startup.linkedin_url && (
                        <a href={startup.linkedin_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-slate-600">
                            <Linkedin className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {(startup.solution_statement || startup.tagline) && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 group relative">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider">About</h2>
                    {isOwner && (
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-slate-800 text-slate-500 hover:text-slate-300"
                        title="Edit description"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">
                    {startup.solution_statement || startup.tagline}
                  </p>
                </div>
              )}
              
              {/* Comments */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">
                  Discussion ({comments.length})
                </h2>
                
                {/* Comment Form */}
                <form onSubmit={handleComment} className="mb-6">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={isAuthenticated ? "Share your thoughts..." : "Sign in to comment"}
                      className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                      disabled={!isAuthenticated}
                      onClick={() => {
                        if (!isAuthenticated) {
                          setLoginAction('comment');
                          setShowLoginModal(true);
                        }
                      }}
                    />
                    <Button
                      type="submit"
                      disabled={!newComment.trim() || submittingComment || !isAuthenticated}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {submittingComment ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </Button>
                  </div>
                </form>
                
                {/* Comments List */}
                <div className="space-y-4">
                  {loadingComments ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No comments yet. Be the first!</p>
                  ) : (
                    comments.map(comment => {
                      const canDelete = user && (
                        comment.person_id === user.user_id || 
                        isSuperAdmin
                      );
                      
                      return (
                        <div key={comment.comment_id} className="flex gap-3 group" data-testid={`comment-${comment.comment_id}`}>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                            {comment.author?.avatar_url ? (
                              <img src={comment.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <span className="text-xs font-bold text-white">
                                {comment.author?.name?.[0] || '?'}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white text-sm">{comment.author?.name || 'Anonymous'}</span>
                                <span className="text-slate-500 text-xs">
                                  {new Date(comment.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteComment(comment.comment_id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-red-400"
                                  title="Delete comment"
                                  data-testid={`delete-comment-${comment.comment_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <p className="text-slate-300 text-sm">{comment.body}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              {/* UPVOTE - Primary CTA */}
              <button
                onClick={handleUpvote}
                disabled={isUpvoting}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  userUpvoted 
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 border-orange-400 text-white shadow-lg shadow-orange-500/20' 
                    : 'bg-slate-900 border-slate-700 hover:border-orange-500 hover:bg-slate-800 text-slate-300 hover:text-orange-400'
                }`}
              >
                <div className="flex items-center justify-center gap-3">
                  {isUpvoting ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <ChevronUp className={`w-7 h-7 ${userUpvoted ? 'text-white' : ''}`} />
                  )}
                  <div className="text-left">
                    <div className={`text-2xl font-bold ${userUpvoted ? 'text-white' : ''}`}>
                      {startup.upvote_count || 0}
                    </div>
                    <div className={`text-xs uppercase tracking-wider ${userUpvoted ? 'text-orange-100' : 'text-slate-500'}`}>
                      {userUpvoted ? 'Upvoted' : 'Upvote'}
                    </div>
                  </div>
                </div>
              </button>
              
              {/* Edit/Claim Button */}
              <Button 
                onClick={handleEditClick}
                variant="outline" 
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                {startup.is_claimed ? (isOwner ? 'Edit Profile' : 'Profile Claimed') : 'Claim this Profile'}
              </Button>
              
              {/* Event Info */}
              {startup.event && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Featured In</h3>
                  <div className="flex items-center gap-2 text-white">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>{startup.event.name}</span>
                  </div>
                  {startup.event.date && (
                    <p className="text-slate-500 text-sm mt-1 ml-6">{startup.event.date}</p>
                  )}
                </div>
              )}
              
              {/* Founder */}
              {startup.founder?.name && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 group relative">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Founder</h3>
                    {isOwner && (
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-slate-800 text-slate-500 hover:text-slate-300"
                        title="Edit founder info"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                      {startup.founder.avatar_url ? (
                        <img src={startup.founder.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold text-white">{startup.founder.name[0]}</span>
                      )}
                    </div>
                    <div>
                      {startup.founder.linkedin_url ? (
                        <a 
                          href={startup.founder.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-white hover:text-blue-400 transition-colors"
                        >
                          {startup.founder.name}
                        </a>
                      ) : (
                        <p className="font-medium text-white">{startup.founder.name}</p>
                      )}
                      {startup.founder.title && (
                        <p className="text-sm text-slate-400">{startup.founder.title}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Company Details */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 group relative">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Details</h3>
                  {isOwner && (
                    <button 
                      onClick={() => setIsEditing(true)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-slate-800 text-slate-500 hover:text-slate-300"
                      title="Edit details"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <dl className="space-y-2 text-sm">
                  {startup.category && (
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Category</dt>
                      <dd className="text-white">{startup.category}</dd>
                    </div>
                  )}
                  {startup.stage && (
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Stage</dt>
                      <dd className="text-white">{startup.stage}</dd>
                    </div>
                  )}
                  {startup.team_size && (
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Team Size</dt>
                      <dd className="text-white">{startup.team_size}</dd>
                    </div>
                  )}
                  {startup.funding_stage && (
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Funding</dt>
                      <dd className="text-white">{startup.funding_stage}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Modals */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        action={loginAction}
      />
      <ClaimModal 
        isOpen={showClaimModal} 
        onClose={() => setShowClaimModal(false)}
        company={startup}
        onSuccess={handleClaimSuccess}
      />
    </div>
  );
};

export default StartupProductPage;
